import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { GitCompareArrows } from 'lucide-react';
import {
  getStockAnalogMock,
  mergeMechanicalLens,
  mergeSectorLens,
  mergeSituationalLens,
  type AnalogLens,
  type StockAnalogLensData,
  type StockAnalogSnapshot,
} from '@/mocks/stockAnalogMock';
import { stockService } from '@/services/api';
import { formatPct, LensAccordion, pctChipClass } from './AnalogLensAccordion';

type Props = {
  ticker: string;
  companyName?: string;
  className?: string;
  /** When set, all three lenses anchor to this session (defaults to latest live data). */
  referenceDate?: string;
  /** Override header catalyst blurb (e.g. today's executive summary). */
  catalystSummary?: string;
  /** Skip duplicate summary paragraph when parent already shows it. */
  omitHeaderSummary?: boolean;
  dayReturnPct?: number | null;
  close?: number | null;
};

export function StockAnalogAnalysisPanel({
  ticker,
  companyName,
  className = '',
  referenceDate,
  catalystSummary,
  omitHeaderSummary = false,
  dayReturnPct,
  close,
}: Props) {
  const mockBase = getStockAnalogMock(ticker, companyName);
  const [snapshot, setSnapshot] = useState<StockAnalogSnapshot>(mockBase);
  const [mechanicalLoading, setMechanicalLoading] = useState(true);
  const [situationalLoading, setSituationalLoading] = useState(true);
  const [sectorLoading, setSectorLoading] = useState(true);
  const [openLens, setOpenLens] = useState<AnalogLens | null>(null);
  const [expandedMatchDate, setExpandedMatchDate] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const base = getStockAnalogMock(ticker, companyName);
    setSnapshot(base);
    setMechanicalLoading(true);
    setSituationalLoading(true);
    setSectorLoading(true);
    setOpenLens(null);
    setExpandedMatchDate(null);

    (async () => {
      const dateOpts = referenceDate ? { date: referenceDate } : undefined;
      const [liveMech, liveSit, liveSector] = await Promise.all([
        stockService.getMechanicalAnalogs(ticker, { topK: 5, ...dateOpts }),
        stockService.getSituationalAnalogs(ticker, dateOpts),
        stockService.getSectorAnalogs(ticker, { topK: 5, ...dateOpts }),
      ]);
      if (cancelled) return;

      let next = base;
      const headerOverrides: Parameters<typeof mergeSituationalLens>[2] = {
        ...(referenceDate ? { referenceDate } : {}),
        ...(typeof dayReturnPct === 'number' ? { dayReturnPct } : {}),
        ...(typeof close === 'number' ? { close } : {}),
        ...(catalystSummary ? { catalystSummary } : {}),
        ...(companyName ? { companyName } : {}),
      };

      if (liveSit) {
        const situational = liveSit.lenses.find((l: StockAnalogLensData) => l.id === 'situational');
        if (situational?.matches?.length) {
          next = mergeSituationalLens(next, situational, {
            ...headerOverrides,
            referenceDate: liveSit.referenceDate || headerOverrides?.referenceDate || next.referenceDate,
            referenceDateLabel: liveSit.referenceDateLabel || next.referenceDateLabel,
            close: headerOverrides?.close ?? liveSit.close ?? next.close,
            dayReturnPct: headerOverrides?.dayReturnPct ?? liveSit.dayReturnPct ?? next.dayReturnPct,
            companyName: companyName || liveSit.companyName || next.companyName,
            catalystSummary: catalystSummary || liveSit.catalystSummary || next.catalystSummary,
          });
        }
      }
      setSituationalLoading(false);

      if (liveMech) {
        const mechanical = liveMech.lenses.find((l: StockAnalogLensData) => l.id === 'mechanical');
        if (mechanical) {
          next = mergeMechanicalLens(next, mechanical, {
            ...headerOverrides,
            referenceDate: next.referenceDate || liveMech.referenceDate,
            referenceDateLabel: next.referenceDateLabel || liveMech.referenceDateLabel,
            close: headerOverrides?.close ?? next.close ?? liveMech.close,
            dayReturnPct: headerOverrides?.dayReturnPct ?? next.dayReturnPct ?? liveMech.dayReturnPct,
            companyName: companyName || next.companyName || liveMech.companyName,
            catalystSummary: catalystSummary || next.catalystSummary || liveMech.catalystSummary,
          });
        }
      }
      setMechanicalLoading(false);

      if (liveSector) {
        const sector = liveSector.lenses.find((l: StockAnalogLensData) => l.id === 'sector');
        if (sector?.matches?.length) {
          next = mergeSectorLens(next, sector, {
            companyName: companyName || next.companyName || liveSector.companyName,
          });
        }
      }
      setSectorLoading(false);

      if (catalystSummary) {
        next = { ...next, catalystSummary };
      }
      if (typeof dayReturnPct === 'number') {
        next = { ...next, dayReturnPct };
      }
      if (typeof close === 'number') {
        next = { ...next, close };
      }

      setSnapshot(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [ticker, companyName, referenceDate, catalystSummary, dayReturnPct, close]);

  const handleLensToggle = (lensId: AnalogLens) => {
    setOpenLens((prev) => {
      if (prev === lensId) {
        setExpandedMatchDate(null);
        return null;
      }
      setExpandedMatchDate(null);
      return lensId;
    });
  };

  const handleMatchToggle = (date: string) => {
    setExpandedMatchDate((prev) => (prev === date ? null : date));
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className={`rounded-3xl border border-gray-200 bg-white overflow-hidden ${className}`}
    >
      <div className="border-b border-gray-100 px-6 py-5 md:px-8 md:py-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-900">
                <GitCompareArrows className="h-4 w-4 text-white" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Historical Analogues
              </span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 tracking-tight">
              {snapshot.referenceDateLabel} — {snapshot.ticker}
            </h3>
            <p className="mt-1 text-sm text-gray-500">{snapshot.companyName}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className={`rounded-lg px-3 py-1.5 text-sm font-medium ${pctChipClass(snapshot.dayReturnPct)} bg-gray-50`}>
              {formatPct(snapshot.dayReturnPct)}
            </span>
            <span className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm text-gray-700">
              ${snapshot.close.toFixed(2)}
            </span>
          </div>
        </div>
        {!omitHeaderSummary ? (
          <p className="mt-4 max-w-3xl text-sm text-gray-600 leading-relaxed">{snapshot.catalystSummary}</p>
        ) : null}
      </div>

      <div className="space-y-2 px-4 py-4 md:px-6 md:py-5">
        {snapshot.lenses.map((lens) => (
          <LensAccordion
            key={lens.id}
            lens={lens}
            isOpen={openLens === lens.id}
            onToggle={() => handleLensToggle(lens.id)}
            expandedMatchDate={openLens === lens.id ? expandedMatchDate : null}
            onToggleMatch={handleMatchToggle}
            isLoading={
              (lens.id === 'mechanical' && mechanicalLoading) ||
              (lens.id === 'situational' && situationalLoading) ||
              (lens.id === 'sector' && sectorLoading)
            }
            referenceDate={snapshot.referenceDate}
          />
        ))}
      </div>
    </motion.section>
  );
}
